// src/components/workspace/investigation/EvidenceLibrary.js

import { useEffect, useMemo, useState } from "react";
import {
  Download,
  FileText,
  Film,
  Image as ImageIcon,
  Paperclip,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { formatClockTime } from "../../../utils/duration";

/**
 * The evidence record for an incident.
 *
 * Replaces the previous files grid, which presented uploads as generic
 * attachments with emoji type icons. Evidence in a paid investigation needs
 * three things the old view did not provide: a thumbnail so you can recognise a
 * screenshot without opening it, clear attribution of who supplied it, and a
 * visible link to the findings that cite it.
 */

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
const TEXT_EXTENSIONS = [
  "txt",
  "log",
  "js",
  "jsx",
  "ts",
  "tsx",
  "py",
  "json",
  "md",
  "csv",
  "yml",
  "yaml",
  "env",
  "sql",
];

const extensionOf = (filename) =>
  (filename || "").split(".").pop()?.toLowerCase() || "";

const isDataUrlOf = (file, kind) =>
  Boolean(file.isSubmissionAttachment && file.downloadURL?.startsWith(`data:${kind}/`));

const isImage = (file) =>
  IMAGE_EXTENSIONS.includes(extensionOf(file.filename)) ||
  isDataUrlOf(file, "image");

const isVideo = (file) =>
  VIDEO_EXTENSIONS.includes(extensionOf(file.filename)) ||
  isDataUrlOf(file, "video");

const isText = (file) => TEXT_EXTENSIONS.includes(extensionOf(file.filename));

function formatSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function Thumbnail({ file }) {
  const [failed, setFailed] = useState(false);

  if (isImage(file) && !failed) {
    return (
      <img
        src={file.downloadURL}
        alt=""
        aria-hidden="true"
        onError={() => setFailed(true)}
        className="h-12 w-12 flex-shrink-0 rounded border border-slate-200 object-cover"
      />
    );
  }

  const Icon = isVideo(file) ? Film : isImage(file) ? ImageIcon : FileText;

  return (
    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded border border-slate-200 bg-slate-50">
      <Icon className="h-5 w-5 text-slate-400" />
    </div>
  );
}

function PreviewModal({ file, onClose }) {
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(false);

  // Text previews need a fetch; images and video render straight from the URL.
  useEffect(() => {
    setText(null);

    if (!file || !isText(file)) return undefined;

    let active = true;
    setLoading(true);

    fetch(file.downloadURL)
      .then((response) => response.text())
      // Truncated because a multi-megabyte log will lock the tab if handed to
      // the DOM whole, and the first pages are what matter for triage.
      .then((body) => active && setText(body.slice(0, 200000)))
      .catch(() => active && setText("This file could not be previewed."))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [file]);

  if (!file) return null;

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-slate-900 bg-opacity-70 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {file.filename}
            </p>
            <p className="text-2xs text-slate-500">
              {file.uploaderName || "Unknown"}
              {file.createdAt ? ` · ${formatClockTime(file.createdAt)}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-slate-50 p-4">
          {isImage(file) ? (
            <img
              src={file.downloadURL}
              alt={file.filename}
              className="mx-auto max-h-full rounded-lg"
            />
          ) : isVideo(file) ? (
            <video
              src={file.downloadURL}
              controls
              className="mx-auto max-h-full rounded-lg"
            />
          ) : loading ? (
            <p className="py-12 text-center text-sm text-slate-500">Loading…</p>
          ) : text !== null ? (
            <pre className="overflow-x-auto rounded-lg bg-white p-3 font-mono text-xs text-slate-800">
              {text}
            </pre>
          ) : (
            <div className="py-12 text-center">
              <p className="mb-3 text-sm text-slate-500">
                This file type cannot be previewed here.
              </p>
              <a
                href={file.downloadURL}
                target="_blank"
                rel="noreferrer"
                download={file.filename}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <Download className="h-4 w-4" />
                Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EvidenceLibrary({
  files,
  findings,
  problem,
  currentUser,
  onUpload,
  onDelete,
  uploading,
}) {
  const [preview, setPreview] = useState(null);

  /**
   * Which findings cite each file. Evidence that nothing references is usually a
   * sign the investigation record is falling behind the conversation.
   */
  const citationsByFileId = useMemo(() => {
    const map = new Map();
    for (const finding of findings || []) {
      for (const fileId of finding.evidenceIds || []) {
        if (!map.has(fileId)) map.set(fileId, []);
        map.get(fileId).push(finding);
      }
    }
    return map;
  }, [findings]);

  const decorated = useMemo(
    () =>
      (files || []).map((file) => ({
        ...file,
        contributor:
          file.uploaderId === problem?.clientId ? "Customer" : "Expert",
        citations: citationsByFileId.get(file.id) || [],
      })),
    [files, problem?.clientId, citationsByFileId]
  );

  const canDelete = (file) =>
    !file.isSubmissionAttachment && file.uploaderId === currentUser?.uid;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <p className="text-xs text-slate-500">
          {decorated.length === 0
            ? "No evidence yet"
            : `${decorated.length} item${decorated.length === 1 ? "" : "s"}`}
        </p>
        <label
          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-slate-900 ${
            uploading ? "cursor-wait opacity-60" : ""
          }`}
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? "Uploading…" : "Add evidence"}
          <input
            type="file"
            onChange={onUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {decorated.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <Paperclip className="h-6 w-6 text-slate-300" />
            <p className="text-sm text-slate-500">
              Screenshots, logs and config exports belong here — anything a
              finding needs to point at.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {decorated.map((file) => (
              <li
                key={file.id}
                className="group flex items-start gap-3 rounded-lg border border-slate-200 p-2.5 transition hover:border-slate-300"
              >
                <button
                  type="button"
                  onClick={() => setPreview(file)}
                  className="flex-shrink-0"
                  aria-label={`Preview ${file.filename}`}
                >
                  <Thumbnail file={file} />
                </button>

                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setPreview(file)}
                    className="block max-w-full truncate text-left text-sm font-medium text-slate-900 transition hover:text-blue-700"
                  >
                    {file.filename}
                  </button>

                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-slate-500">
                    <span
                      className={`rounded px-1.5 py-0.5 font-semibold uppercase tracking-wide ${
                        file.contributor === "Customer"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-violet-50 text-violet-700"
                      }`}
                    >
                      {file.contributor}
                    </span>
                    <span>{file.uploaderName || "Unknown"}</span>
                    {file.createdAt && (
                      <span>{formatClockTime(file.createdAt)}</span>
                    )}
                    {formatSize(file.size) && <span>{formatSize(file.size)}</span>}
                    {file.isSubmissionAttachment && (
                      <span className="text-slate-400">from intake</span>
                    )}
                  </div>

                  {file.citations.length > 0 && (
                    <p className="mt-1 text-2xs text-slate-500">
                      Cited by {file.citations.length} finding
                      {file.citations.length === 1 ? "" : "s"}
                    </p>
                  )}
                </div>

                <div className="flex flex-shrink-0 items-center gap-1">
                  <a
                    href={file.downloadURL}
                    target="_blank"
                    rel="noreferrer"
                    download={
                      file.isSubmissionAttachment ? undefined : file.filename
                    }
                    className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label={`Download ${file.filename}`}
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  {canDelete(file) && (
                    <button
                      type="button"
                      onClick={() => onDelete(file)}
                      className="rounded p-1.5 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100"
                      aria-label={`Delete ${file.filename}`}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <PreviewModal file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
